import React from 'react';
import { styled } from '@mui/material/styles';
import {
  Tabs,
  Tab,
  CardContent,
  Card,
  LinearProgress,
  Box,
  Button,
} from '@mui/material';

import { useAppSelector } from '../../../hooks/useAppSelector';
import { useCommonTranslation } from '../../../hooks/useCommonTranslation';

import { Reservation } from '../../../data/models/reservation.model';
import { Passenger } from '../../../data/models/passenger.model';
import { ServicePlan } from '../../../data/models/service-plan.model';
import {
  selectLoadingCurrentPassengerFutureReservations,
  selectLoadingCurrentPassengerPastReservations,
} from '../../../data/passenger.slice';

import TabPanel from '../../TabPanel';
import ReservationTable from './ReservationTable';

const PREFIX = 'PassengerReservation';

const classes = {
  container: `${PREFIX}-container`,
  loadMoreContainer: `${PREFIX}-loadMoreContainer`,
};

const StyledCard = styled(Card)(({ theme }) => ({
  [`&.${classes.container}`]: {
    width: '100%',
  },
  [`& .${classes.loadMoreContainer}`]: {
    display: 'flex',
    justifyContent: 'center',
    padding: theme.spacing(2),
  },
}));

interface PassengerReservationProps {
  passenger: Passenger | null;
  pastReservations: { reservations: Reservation[]; continuationToken?: string };
  futureReservations: {
    reservations: Reservation[];
    continuationToken?: string;
  };
  servicePlans: ServicePlan[];
  loadingPassenger: boolean;
  onLoadMorePastReservations?: () => void;
  onLoadMoreFutureReservations?: () => void;
}

export default function PassengerReservations({
  passenger,
  pastReservations,
  futureReservations,
  servicePlans,
  loadingPassenger,
  onLoadMorePastReservations,
  onLoadMoreFutureReservations,
}: PassengerReservationProps) {
  const t = useCommonTranslation();

  const [tabIndex, setTabIndex] = React.useState(0);

  const futureReservationsLoading = useAppSelector(
    selectLoadingCurrentPassengerFutureReservations,
  );
  const pastReservationsLoading = useAppSelector(
    selectLoadingCurrentPassengerPastReservations,
  );

  const isLoading = futureReservationsLoading || pastReservationsLoading;

  const handleTabChange = (_evt: React.ChangeEvent<{}>, newValue: number) => {
    setTabIndex(newValue);
  };

  return (
    <StyledCard className={classes.container}>
      <CardContent>
        <Tabs
          indicatorColor="primary"
          textColor="primary"
          value={tabIndex}
          onChange={handleTabChange}
          variant="standard">
          <Tab label={t('passenger.upcoming')} data-testid="tab-one" />
          <Tab label={t('passenger.history')} data-testid="tab-two" />
        </Tabs>
        <TabPanel value={tabIndex} index={0}>
          {isLoading && <LinearProgress />}
          <ReservationTable
            passenger={passenger}
            reservations={futureReservations}
            servicePlans={servicePlans}
            loadingPassenger={loadingPassenger}
          />
          {futureReservations.continuationToken &&
            onLoadMoreFutureReservations && (
              <Box className={classes.loadMoreContainer}>
                <Button
                  variant="outlined"
                  color="primary"
                  disabled={futureReservationsLoading}
                  onClick={onLoadMoreFutureReservations}>
                  {t('common.loadMore')}
                </Button>
              </Box>
            )}
        </TabPanel>
        <TabPanel value={tabIndex} index={1}>
          {isLoading && <LinearProgress />}
          <ReservationTable
            passenger={passenger}
            reservations={pastReservations}
            servicePlans={servicePlans}
            loadingPassenger={loadingPassenger}
          />
          {pastReservations.continuationToken && onLoadMorePastReservations && (
            <Box className={classes.loadMoreContainer}>
              <Button
                variant="outlined"
                color="primary"
                disabled={pastReservationsLoading}
                onClick={onLoadMorePastReservations}>
                {t('common.loadMore')}
              </Button>
            </Box>
          )}
        </TabPanel>
      </CardContent>
    </StyledCard>
  );
}
